import React, { useEffect, useState } from "react";
import { View, StyleSheet, KeyboardAvoidingView, Platform } from "react-native";
import { Color, globalFontStyle } from "../../../assets";
import Colors from "../../../assets/Color/Colors";
import Width from "../../../assets/Width";
import { useDispatch } from "react-redux";
import {Button} from "../../../components/core/Button";
import {Input} from "../../../components/core/Input";
import { useForm, useFormState } from "react-hook-form";
import {
  RegEx,
  trackMoengageEvent,
  isMobile,
} from "../../../utils/commonMethods";
import { Network } from "../../../utils/network";
import { getBankDetails } from "../redux/profile.action";
import FormTextLabel from "../../../commonComponents/FormTextLabel";
import {globalConfigurableData} from "../../../utils/globalConstant";

const AddBankDetails = ({ BankDetailsData = null }) => {
  const {
    control,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({ mode: "onChange" });
  const { dirtyFields } = useFormState({
    control,
  });

  const accountNumber = watch("accountNumber");
  const accountNumberCheck = watch("accountNumberCheck");
  const dispatch = useDispatch();

  useEffect(() => {
    const bankdetails = BankDetailsData?.bankdetails;
    const PAN = bankdetails?.number;
    const accountHolderName = bankdetails?.name;
    const ifsc = bankdetails?.ifsc;
    const accountNumber = bankdetails?.accountnumber;
    let defaultValues = {};
    defaultValues.PANNO = PAN;
    defaultValues.ifsc = ifsc;
    defaultValues.accountNumber = accountNumber;
    defaultValues.accountNumberCheck = accountNumber;

    reset({ ...defaultValues });
  }, [BankDetailsData?.bankdetails?.number]);

  const formatPanNumber = (value) => {
    if (!value) return value;
    return value.toUpperCase();
  };

  const formatIfscCode = (value) => {
    if (!value) return value;
    return value.toUpperCase();
  };

  const { isDirty } = useFormState({
    control,
  });

  const isKycVerified = BankDetailsData?.bankdetails?.kycstatus;

  const [apiSuccess, setApiSuccess] = useState("");
  const [respError, setResponseError] = useState("");

  const onClickBankUpdate = async (details) => {
    if (isDirty) {
      const data = {
        accountNumber: details.accountNumberCheck,
        ifsc: details.ifsc,
        pan: details.PANNO,
      };

      const res = await Network.addBankDetails(data);
      if (!res?.data?.success) {
        dispatch(getBankDetails());
        setResponseError(res?.data?.error);
      } else {
        dispatch(getBankDetails());
        trackMoengageEvent("KYC Details", {
          Status: "complete",
          PAN: details.PANNO,
          IFSC: details.ifsc,
          AccountNo: details.accountNumberCheck,
          Time: new Date(),
        });
        setApiSuccess("Your KYC verification has been successfull!");
      }
    } else {
      setResponseError("Nothing to Update");
    }
  };

  useEffect(() => {
    if (isDirty) {
      setResponseError("");
    }
  }, [isDirty]);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={0}>
      <View style={styles.contentContainer}>
        <View style={styles.headerContainer}>
          <FormTextLabel
            labelStyles={{
              ...globalFontStyle.appFontSubTextBold,
              color: globalConfigurableData.primaryTextColor1,
              fontSize: isMobile() ? 14 : 16,
              lineHeight: isMobile() ? 14 : 16,
              marginBottom: 8,
            }}
            label={"KYC Details"}
          />
          <FormTextLabel
            labelStyles={{
              ...globalFontStyle.appFontSmallText,
              color: Colors.grey7,
              marginBottom: 16,
            }}
            label={"Please make sure that your bank details are matching with your KYC details for a hassel-free withdrawal process. We'll deposit 1 in order to verify your bank account."}
          />
        </View>

        <Input
          name="PANNO"
          placeholder="Write PAN number here"
          control={control}
          label="PAN Number"
          variant="default"
          size="default"
          formatter="pan"
          rules={{
            required: "Please enter PAN No",
            pattern: {
              value: RegEx.pan,
              message: "Please enter a valid PAN",
            },
          }}
        />

        <Input
          name="ifsc"
          placeholder="Write IFSC Code here"
          control={control}
          label="IFSC Code"
          variant="default"
          size="default"
          formatter="ifsc"
          rules={{
            required: "Please enter IFSC code",
            pattern: {
              value: RegEx.ifsc,
              message: "Please enter a valid IFSC",
            },
          }}
        />

        <Input
          name="accountNumber"
          placeholder="Please enter a valid Account Number"
          control={control}
          label="Account Number"
          variant="default"
          size="default"
          numeric={true}
          rules={{
            required: "Please enter account number",
            pattern: {
              value: RegEx.accountNum,
              message: "Please enter a valid Account Number",
            },
          }}
        />

        <Input
          name="accountNumberCheck"
          placeholder="Re-enter Account Number"
          control={control}
          label="Confirm Account Number"
          variant="default"
          size="default"
          numeric={true}
          rules={{
            required: true,
            validate: {
              equal: (value) => {
                return value === accountNumber || "The account numbers don't match";
              },
            },
          }}
        />

        <FormTextLabel
          labelStyles={styles.infoText}
          label={"Please double-check your details before submitting to avoid delays in withdrawal. Please note that PAN details cannot be changed post-submission."}
        />

        {respError && (
          <FormTextLabel 
            labelStyles={styles.errorText} 
            label={respError} 
          />
        )}
        {apiSuccess && (
          <FormTextLabel 
            labelStyles={styles.successText} 
            label={apiSuccess} 
          />
        )}

        <View style={styles.buttonContainer}>
          <Button
            variant="primary"
            onPress={handleSubmit(onClickBankUpdate)}
            translate={true}
          >
            Submit
          </Button>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};

export default AddBankDetails;

const styles = StyleSheet.create({
  contentContainer: {
    paddingTop: 16,
    backgroundColor: globalConfigurableData.cardBgColor,
  },
  headerContainer: {
    marginBottom: 16,
  },
  successText: {
    ...globalFontStyle.appFontSmallText,
    color: "#00aa51",
    marginBottom: 16,
  },
  errorText: {
    ...globalFontStyle.appFontSmallText,
    color: "#FF0000",
    marginBottom: 16,
  },
  infoText: {
    ...globalFontStyle.appFontSmallText,
    color: Colors.grey,
    marginBottom: 20,
  },
  buttonContainer: {
    marginBottom: 20,
  },
});
